"use client"

import { useState } from "react"
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Alert,
  Fade,
  Zoom,
  Slide,
  Snackbar,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material"
import { styled, keyframes } from "@mui/system"
import { useNavigate, useSearchParams } from "react-router-dom"
import PersonIcon from "@mui/icons-material/Person"
import LockIcon from "@mui/icons-material/Lock"
import EmailIcon from "@mui/icons-material/Email"
import VisibilityIcon from "@mui/icons-material/Visibility"
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff"
import LoginIcon from "@mui/icons-material/Login"
import PersonAddIcon from "@mui/icons-material/PersonAdd"
import StarIcon from "@mui/icons-material/Star"
import GamepadIcon from "@mui/icons-material/Gamepad"
import LockOutlinedIcon from "@mui/icons-material/LockOutlined"
import { API_URL, REGISTRATION_OPEN, CONTACT_EMAIL } from './config';

// אנימציות
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7);
  }
  70% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
  }
`

const gentleGlowAnimation = keyframes`
  0%, 100% {
    text-shadow: 0 0 5px rgba(255, 255, 255, 0.3), 0 0 10px rgba(33, 150, 243, 0.2);
  }
  50% {
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.5), 0 0 15px rgba(33, 150, 243, 0.3);
  }
`

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`

const sparkleAnimation = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`

const slideInAnimation = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`

// קונטיינר ראשי
const LoginContainer = styled(Box)({
  minHeight: "100vh",
  height: "100vh",
  width: "100vw",
  fontFamily: "'Assistant','Segoe UI',sans-serif",
  background:
    "radial-gradient(circle at 28% 18%, #26306e 0%, transparent 55%), radial-gradient(circle at 78% 82%, #3a1a5e 0%, transparent 55%), linear-gradient(160deg,#0b1330 0%,#141b46 50%,#231452 100%)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "hidden",
  padding: "1rem",
  boxSizing: "border-box",
})

// כותרת ראשית
const MainTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Assistant','Segoe UI',sans-serif",
  fontSize: "2rem",
  fontWeight: 900,
  textAlign: "center",
  background: "linear-gradient(135deg,#9CFFC9,#2fd47a)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  filter: "drop-shadow(0 3px 14px rgba(0,0,0,.4))",
  [theme.breakpoints.up("sm")]: {
    fontSize: "2.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2.8rem",
  },
}))

// כרטיס הלוגין
const LoginCard = styled(Card)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.06)",
  backdropFilter: "blur(14px)",
  border: "1px solid rgba(255, 255, 255, 0.18)",
  borderRadius: "28px",
  padding: "2rem 2rem 0.9rem",
  width: "100%",
  maxWidth: "400px",
  color: "#fff",
  boxShadow: "0 24px 70px rgba(0,0,0,.55), inset 0 1px 1px rgba(255,255,255,.25)",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "450px",
    padding: "2.5rem 2.5rem 1rem",
  },
}))

// שדה טקסט מעוצב
const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: "rgba(255, 255, 255, 0.08)",
    backdropFilter: "blur(10px)",
    borderRadius: "15px",
    color: "#fff",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.2)",
      borderWidth: "1.5px",
    },
    "&:hover fieldset": {
      borderColor: "rgba(124, 255, 183, 0.5)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#7CFFB7",
      boxShadow: "0 0 12px rgba(124, 255, 183, 0.35)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.7)",
    "&.Mui-focused": {
      color: "#7CFFB7",
    },
  },
  "& .MuiInputBase-input": {
    color: "#fff",
  },
}))

// כפתור מעוצב
const StyledButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #5df2a8, #16a34a)",
  border: "none",
  borderRadius: "999px",
  color: "#052616",
  fontFamily: "'Assistant','Segoe UI',sans-serif",
  fontSize: "1.15rem",
  fontWeight: 800,
  padding: "12px 30px",
  margin: "1rem 0",
  width: "100%",
  boxShadow: "0 10px 26px rgba(34,197,94,.32)",
  textShadow: "none",
  "&:hover": {
    background: "linear-gradient(135deg, #4be89a, #15912f)",
    transform: "translateY(-2px)",
    boxShadow: "0 14px 34px rgba(34,197,94,.42)",
  },
  "&:disabled": {
    background: "rgba(255, 255, 255, 0.25)",
    color: "rgba(255,255,255,.6)",
  },
}))

// כפתור משני
const SecondaryButton = styled(Button)({
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255, 255, 255, 0.22)",
  borderRadius: "999px",
  color: "#eaf1ff",
  fontFamily: "'Assistant','Segoe UI',sans-serif",
  fontSize: "1rem",
  fontWeight: 700,
  padding: "10px 25px",
  margin: "0.5rem 0",
  width: "100%",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.12)",
    borderColor: "rgba(124, 255, 183, 0.5)",
  },
})

// קומפוננטת כוכבים
const SparkleEffect = () => {
  return (
    <>
      {[...Array(22)].map((_, i) => {
        const s = Math.random() * 5 + 4
        return (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: s,
              height: s,
              borderRadius: "50%",
              background: "#fff",
              boxShadow: "0 0 12px 2px rgba(255,255,255,.85)",
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `${sparkleAnimation} ${2 + Math.random() * 2.5}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
              zIndex: 1,
            }}
          />
        )
      })}
    </>
  )
}


const LoginPage = () => {
  const [searchParams] = useSearchParams()
  const [isLogin, setIsLogin] = useState(searchParams.get('tab') !== 'register')
  const registrationClosed = !REGISTRATION_OPEN
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const navigate = useNavigate()

  // Password reset state
  const [showReset, setShowReset] = useState(false)
  const [resetStep, setResetStep] = useState(0) // 0=identifier, 1=code, 2=new password
  const [resetIdentifier, setResetIdentifier] = useState("")
  const [resetCode, setResetCode] = useState("")
  const [resetNewPassword, setResetNewPassword] = useState("")
  const [resetMaskedPhone, setResetMaskedPhone] = useState("")

  // נתוני לוגין
  const [loginData, setLoginData] = useState({
    username: "",
    password: "",
  })

  // נתוני הרשמה
  const [registerData, setRegisterData] = useState({
    username: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
  })

  // פונקציית לוגין
  const handleLogin = async () => {
    if (!loginData.username || !loginData.password) {
      setError("אנא מלא את כל השדות")
      return
    }

    setLoading(true)
    setError("")

    try {
      const response = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user: loginData.username,
          password: loginData.password,
        }),
      })

      const data = await response.json()

      if (data.status == "success") {
        // שמירת טוקן JWT ב-localStorage
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setSuccess("ברוך הבא " + data.user.first_name)
        // מעבר למסך המשחק עם מספר המשחק
        setTimeout(() => {
          navigate(`/panel`)
        }, 1500)
      } else {
        setError(data.message || "שם משתמש או סיסמה שגויים")
      }
    } catch (error) {
      setError("שגיאה בהתחברות לשרת")
      console.error("Login error:", error)
    } finally {
      setLoading(false)
    }
  }

  // פונקציית שליחת קוד איפוס
  const handleSendResetCode = async () => {
    if (!resetIdentifier) { setError("הקלד טלפון או אימייל"); return }
    setLoading(true); setError("")
    try {
      const res = await fetch(`${API_URL}/send-reset-code`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: resetIdentifier }),
      })
      const data = await res.json()
      if (data.status) { setResetMaskedPhone(data.maskedPhone); setResetStep(1); setSuccess("קוד אימות נשלח ב-SMS") }
      else setError(data.error || "שגיאה")
    } catch { setError("שגיאה בהתחברות לשרת") }
    finally { setLoading(false) }
  }

  // פונקציית איפוס סיסמה
  const handleResetPassword = async () => {
    if (!resetCode || !resetNewPassword) { setError("מלא את כל השדות"); return }
    if (resetNewPassword.length < 6) { setError("הסיסמה חייבת להכיל לפחות 6 תווים"); return }
    setLoading(true); setError("")
    try {
      const res = await fetch(`${API_URL}/reset-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: resetIdentifier, code: resetCode, newPassword: resetNewPassword }),
      })
      const data = await res.json()
      if (data.status) {
        setSuccess("הסיסמה שונתה בהצלחה! מתחבר...")
        setTimeout(() => { setShowReset(false); setResetStep(0); setSuccess(""); setResetCode(""); setResetNewPassword("") }, 2000)
      } else setError(data.error || "שגיאה")
    } catch { setError("שגיאה בהתחברות לשרת") }
    finally { setLoading(false) }
  }

  // פונקציית הרשמה
  const handleRegister = async () => {
    if (!registerData.username || !registerData.lastName || !registerData.email || !registerData.phone || !registerData.password || !registerData.confirmPassword) {
      setError("אנא מלא את כל השדות")
      return
    }

    if (registerData.password !== registerData.confirmPassword) {
      setError("הסיסמאות אינן תואמות")
      return
    }

    if (registerData.password.length < 6) {
      setError("הסיסמה חייבת להכיל לפחות 6 תווים")
      return
    }

    setLoading(true)
    setError("")

    try {
      const response = await fetch(`${API_URL}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          first_name: registerData.username,
          last_name: registerData.lastName,
          email: registerData.email,
          phone: registerData.phone,
          password: registerData.password,
        }),
      })

      const data = await response.json()

      if (data.status == "success") {
        // שמירת טוקן JWT ב-localStorage
        if (data.token) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        setSuccess("נרשמת בהצלחה! מעביר לממשק...")
        setTimeout(() => {
          navigate(`/panel?tour=1`)
        }, 1200)
      } else {
        setError(data.message || "שגיאה בהרשמה")
      }
    } catch (error) {
      setError("שגיאה בהתחברות לשרת")
      console.error("Register error:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <LoginContainer>
      {/* אפקטי כוכבים */}
      <SparkleEffect />

      {/* תוכן מרכזי */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          maxWidth: isLogin || registrationClosed ? "500px" : "640px",
          transition: "max-width .3s ease",
          zIndex: 5,
        }}
      >
        {/* כותרת */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <MainTitle>{isLogin ? "התחברות למשחק" : registrationClosed ? "ההרשמה סגורה" : "הרשמה למשחק"}</MainTitle>
        </Box>

        {/* כרטיס הלוגין/הרשמה */}
        <Zoom in timeout={1000}>
          <LoginCard sx={{ maxWidth: isLogin || registrationClosed ? { xs: "100%", sm: "450px" } : { xs: "100%", sm: "600px" } }}>
            <CardContent sx={{ padding: 0 }}>
              {/* טופס לוגין */}
              {isLogin ? (
                <Box sx={{ animation: `${slideInAnimation} 0.5s ease-out` }}>
                  <StyledTextField
                    fullWidth
                    label="שם משתמש"
                    variant="outlined"
                    margin="normal"
                    value={loginData.username}
                    onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <StyledTextField
                    fullWidth
                    label="סיסמה"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    margin="normal"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <StyledButton
                    onClick={handleLogin}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                  >
                    {loading ? "מתחבר..." : "התחבר"}
                  </StyledButton>

                  <SecondaryButton onClick={() => setIsLogin(false)} sx={{ mb: 0 }}>
                    {registrationClosed ? "רוצים לפתוח חשבון?" : "אין לך חשבון? הירשם"}
                  </SecondaryButton>
                  <Box sx={{ textAlign: "center", mt: 1.75 }}>
                    <Button
                      onClick={() => { setShowReset(true); setResetStep(0); setError(""); setSuccess(""); }}
                      sx={{ color: "rgba(255,255,255,.6)", fontSize: ".82rem", pt: 0.5, minHeight: 0, textDecoration: "underline", "&:hover": { color: "#7CFFB7", background: "transparent" } }}
                    >
                      שכחת סיסמה?
                    </Button>
                  </Box>
                </Box>
              ) : registrationClosed ? (
                // ההרשמה סגורה - הפניה למייל יצירת קשר
                <Box sx={{ animation: `${slideInAnimation} 0.5s ease-out`, textAlign: "center" }}>
                  <LockOutlinedIcon sx={{ fontSize: 52, color: "#7CFFB7", mb: 1 }} />

                  <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.3rem", mb: 1.5 }}>
                    ההרשמה סגורה כרגע
                  </Typography>

                  <Typography sx={{ color: "rgba(255,255,255,.75)", fontSize: ".95rem", lineHeight: 1.8, mb: 2.5 }}>
                    לא ניתן לפתוח כרגע חשבון חדש באתר.
                    <br />
                    לפרטים ולפתיחת חשבון אפשר לפנות אלינו במייל:
                  </Typography>

                  <Button
                    component="a"
                    href={`mailto:${CONTACT_EMAIL}`}
                    startIcon={<EmailIcon />}
                    sx={{
                      background: "rgba(124,255,183,.12)",
                      border: "1px solid rgba(124,255,183,.45)",
                      borderRadius: "999px",
                      color: "#9CFFC9",
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      padding: "10px 26px",
                      textTransform: "none",
                      direction: "ltr",
                      "&:hover": { background: "rgba(124,255,183,.2)" },
                    }}
                  >
                    {CONTACT_EMAIL}
                  </Button>

                  <SecondaryButton onClick={() => setIsLogin(true)} sx={{ mt: 3 }}>יש לך כבר חשבון? התחבר כאן</SecondaryButton>
                </Box>
              ) : (
                // טופס הרשמה
                <Box sx={{ animation: `${slideInAnimation} 0.5s ease-out` }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 1.5, rowGap: 0.5 }}>
                  <StyledTextField
                    fullWidth
                    label="שם פרטי"
                    variant="outlined"
                    margin="normal"
                    value={registerData.username}
                    onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <StyledTextField
                    fullWidth
                    label="שם משפחה"
                    variant="outlined"
                    margin="normal"
                    value={registerData.lastName}
                    onChange={(e) => setRegisterData({ ...registerData, lastName: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <StyledTextField
                    fullWidth
                    label="טלפון"
                    variant="outlined"
                    margin="normal"
                    value={registerData.phone}
                    onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <StyledTextField
                    fullWidth
                    label="אימייל"
                    type="email"
                    variant="outlined"
                    margin="normal"
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <StyledTextField
                    fullWidth
                    label="סיסמה"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    margin="normal"
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <StyledTextField
                    fullWidth
                    label="אישור סיסמה"
                    type={showConfirmPassword ? "text" : "password"}
                    variant="outlined"
                    margin="normal"
                    value={registerData.confirmPassword}
                    onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                          >
                            {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  </Box>

                  <StyledButton
                    onClick={handleRegister}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
                  >
                    {loading ? "נרשם..." : "הירשם"}
                  </StyledButton>

                  <SecondaryButton onClick={() => setIsLogin(true)}>יש לך כבר חשבון? התחבר כאן</SecondaryButton>
                </Box>
              )}
            </CardContent>
          </LoginCard>
        </Zoom>

        {/* === Password Reset Dialog === */}
        {showReset && (
          <Fade in>
            <Box sx={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "rgba(0,0,0,.7)", zIndex: 9999,
            }}>
              <LoginCard sx={{ animation: "none", maxWidth: 420 }}>
                <CardContent sx={{ p: 0 }}>
                  <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#fff", textAlign: "center", mb: 2 }}>
                    🔐 איפוס סיסמה
                  </Typography>

                  {error && <Fade in><Alert severity="error" sx={{ mb: 2, borderRadius: "15px" }}>{error}</Alert></Fade>}
                  {success && <Fade in><Alert severity="success" sx={{ mb: 2, borderRadius: "15px" }}>{success}</Alert></Fade>}

                  {resetStep === 0 && (
                    <Box>
                      <Typography sx={{ color: "rgba(255,255,255,.7)", fontSize: ".9rem", mb: 2, textAlign: "center" }}>
                        הקלד את מספר הטלפון או האימייל שלך ונשלח לך קוד אימות ב-SMS
                      </Typography>
                      <StyledTextField
                        fullWidth label="טלפון / אימייל" variant="outlined" margin="normal"
                        value={resetIdentifier}
                        onChange={(e) => setResetIdentifier(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSendResetCode()}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: "rgba(255,255,255,.7)" }} /></InputAdornment>,
                        }}
                      />
                      <StyledButton onClick={handleSendResetCode} disabled={loading}>
                        {loading ? "שולח..." : "📲 שלח קוד אימות"}
                      </StyledButton>
                    </Box>
                  )}

                  {resetStep === 1 && (
                    <Box>
                      <Typography sx={{ color: "rgba(255,255,255,.7)", fontSize: ".9rem", mb: 2, textAlign: "center" }}>
                        קוד נשלח למספר {resetMaskedPhone}
                      </Typography>
                      <StyledTextField
                        fullWidth label="קוד אימות" variant="outlined" margin="normal"
                        value={resetCode}
                        onChange={(e) => setResetCode(e.target.value)}
                        inputProps={{ maxLength: 6, style: { letterSpacing: 10, textAlign: "center", fontWeight: 800, fontSize: "1.5rem" } }}
                      />
                      <StyledTextField
                        fullWidth label="סיסמה חדשה" type="password" variant="outlined" margin="normal"
                        value={resetNewPassword}
                        onChange={(e) => setResetNewPassword(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: "rgba(255,255,255,.7)" }} /></InputAdornment>,
                        }}
                      />
                      <StyledButton onClick={handleResetPassword} disabled={loading}>
                        {loading ? "מאפס..." : "🔑 שנה סיסמה"}
                      </StyledButton>
                    </Box>
                  )}

                  <SecondaryButton onClick={() => { setShowReset(false); setError(""); setSuccess("") }}>
                    חזרה להתחברות
                  </SecondaryButton>
                </CardContent>
              </LoginCard>
            </Box>
          </Fade>
        )}
      </Box>

      {/* אפקטים דקורטיביים */}
      <Box
        sx={{
          position: "absolute",
          top: "15%",
          left: "10%",
          width: { xs: "80px", sm: "120px" },
          height: { xs: "80px", sm: "120px" },
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(124, 255, 183, 0.28) 0%, transparent 70%)",
          animation: `${floatAnimation} 4s ease-in-out infinite`,
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          bottom: "20%",
          right: "10%",
          width: { xs: "100px", sm: "150px" },
          height: { xs: "100px", sm: "150px" },
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(45, 212, 122, 0.22) 0%, transparent 70%)",
          animation: `${floatAnimation} 5s ease-in-out infinite reverse`,
          zIndex: 0,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          top: "60%",
          left: "5%",
          width: { xs: "60px", sm: "80px" },
          height: { xs: "60px", sm: "80px" },
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(124, 255, 183, 0.18) 0%, transparent 70%)",
          animation: `${floatAnimation} 6s ease-in-out infinite`,
          zIndex: 0,
        }}
      />

      {/* הודעה שקופצת מלמטה */}
      <Snackbar
        open={!showReset && !!(error || success)}
        autoHideDuration={4000}
        onClose={() => { setError(""); setSuccess("") }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="up" />}
      >
        <Alert
          severity={error ? "error" : "success"}
          variant="filled"
          onClose={() => { setError(""); setSuccess("") }}
          sx={{
            borderRadius: 3, fontWeight: 700, fontFamily: "'Assistant','Segoe UI',sans-serif",
            fontSize: "1rem", boxShadow: "0 12px 34px rgba(0,0,0,.4)",
            ...(error ? {} : { background: "linear-gradient(135deg,#16a34a,#22c55e)" }),
          }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </LoginContainer>
  )
}

export default LoginPage
